import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { useVisibleMessages } from '../../hooks/use-visible-messages';
import { useToolEvents } from '../../lib/tool-stream';
import { ChatFeedbackProvider, ChatReadOnlyProvider } from './ChatFeedbackContext';
import { ChatMessageView } from './ChatMessage';
import { WhimsicalLoadingIndicator } from './WhimsicalLoadingIndicator';
/**
 * Memoized chat message component to prevent unnecessary re-renders
 */ const MemoChatMessage = /*#__PURE__*/ memo(ChatMessageView, (prev, next)=>prev.message === next.message && prev.chatId === next.chatId && prev.variant === next.variant);
/**
 * Renders a thread of chat messages with optimizations for streaming
 * @param props Component props
 * @returns Chat thread component
 */ export function ChatThread({ messages, isSending, chatId, variant = 'default', onFeedback, readOnly = false }) {
    // Listen for optimistic save-log messages
    const { events: optimistic } = useToolEvents((ev)=>ev.type === 'data-chat-log-save-optimistic');
    // Filter messages to only show visible ones
    const visibleMessages = useVisibleMessages(messages);
    // Add optimistic messages to the end of the visible messages
    const withOptimistic = useMemo(()=>{
        if (optimistic.length === 0) return visibleMessages;
        const last = optimistic[optimistic.length - 1];
        if (last?.message) {
            return [
                ...visibleMessages,
                last.message
            ];
        }
        return visibleMessages;
    }, [
        visibleMessages,
        optimistic
    ]);
    const lastMessage = withOptimistic[withOptimistic.length - 1];
    const requestText = [
        ...withOptimistic
    ].reverse().find((message)=>message.role === 'user')?.parts.filter((part)=>part.type === 'text').map((part)=>part.type === 'text' ? part.text : '').join(' ') ?? '';
    const requestStatus = /pric|budget|cost|fund|excel|workbook/i.test(requestText) ? 'Preparing your pricing and funding response' : /compliance|requirement|rfp|solicitation/i.test(requestText) ? 'Preparing your requirements and compliance response' : /slide|brief|leadership/i.test(requestText) ? 'Preparing your leadership briefing' : /contract|deliver|staff|transition/i.test(requestText) ? 'Preparing your contract delivery response' : 'Working through your request';
    // Message objects retain identity during streaming, so memoized rows skip
    // unchanged content while saved decisions can still update the same row.
    const historyToRender = useMemo(()=>withOptimistic.slice(0, -1), [
        withOptimistic
    ]);
    return /*#__PURE__*/ _jsx(ChatReadOnlyProvider, {
        value: readOnly,
        children: /*#__PURE__*/ _jsx(ChatFeedbackProvider, {
            value: readOnly ? null : onFeedback ?? null,
            children: /*#__PURE__*/ _jsxs("div", {
                className: `${variant === 'assist' ? 'space-y-4' : variant === 'specra' ? 'space-y-[22px]' : 'space-y-8'}`,
                children: [
                    historyToRender.map((m)=>/*#__PURE__*/ _jsx(MemoChatMessage, {
                            message: m,
                            chatId: chatId ?? '',
                            variant: variant
                        }, m.id)),
                    lastMessage && /*#__PURE__*/ _jsx(ChatMessageView, {
                        message: lastMessage,
                        chatId: chatId ?? '',
                        variant: variant,
                        isActive: Boolean(isSending && lastMessage.role === 'assistant')
                    }, lastMessage.id),
                    isSending && /*#__PURE__*/ _jsx(WhimsicalLoadingIndicator, {
                        variant: variant,
                        customMessages: [
                            lastMessage?.parts.some((part)=>'toolName' in part && part.toolName === 'read-document-content') ? 'Preparing document content for your review' : requestStatus
                        ]
                    })
                ]
            })
        })
    });
}
